// frontend/src/components/ConnectedApps.js
//
// "Connected apps" — the user-facing half of the MCP connector: which AI
// clients hold a read grant on this account, and a way to cut them off.
//
// This is the only place a user can revoke a connector, so it deliberately
// states the two things they need in order to decide: what the connection can
// see (personal vs lab scope) and when it was last used. A grant that has never
// been used looks very different from one that ran an hour ago, and that
// difference is what makes an unexpected entry actionable.
//
// Revocation takes effect on the connector's very next request — the server
// checks revoked_at on every MCP call rather than caching it — so there is no
// "may take a few minutes" caveat to give.
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useToast } from '../hooks/useToast';
import { ConfirmDialog } from './ConfirmDialog';

const ConnectedApps = () => {
  const toast = useToast();
  const [connections, setConnections] = useState([]);
  // Comes from the server, never from REACT_APP_API_URL. That build-time value
  // is whatever origin the SPA talks to (typically the raw Cloud Run host); the
  // connector URL must be the backend's PUBLIC_BASE_URL, because that is what
  // the OAuth metadata advertises as the resource identifier. Showing the wrong
  // one does not just look unbranded — a user who copies it gets a mismatch
  // between the URL they entered and the resource discovery returns.
  const [mcpUrl, setMcpUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pendingDisconnect, setPendingDisconnect] = useState(null);
  const [disconnectingId, setDisconnectingId] = useState(null);

  const fetchConnections = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/oauth/connections`);
      setConnections(res.data?.connections || []);
      setMcpUrl(res.data?.mcp_url || null);
    } catch (err) {
      setError('Could not load connected apps.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConnections();
  }, [fetchConnections]);

  const handleDisconnect = async (connection) => {
    setPendingDisconnect(null);
    setDisconnectingId(connection.client_id);
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/oauth/connections/${connection.client_id}`
      );
      toast?.success?.(`${connection.client_name} disconnected.`);
      await fetchConnections();
    } catch (err) {
      toast?.error?.('Could not disconnect that app. Please try again.');
    } finally {
      setDisconnectingId(null);
    }
  };

  // Absolute date plus a relative hint: "3 days ago" answers "is this stale?"
  // at a glance, while the exact timestamp is what someone would quote when
  // reporting a connection they do not recognise.
  const formatUsed = (value) => {
    if (!value) return 'Never used';
    const then = new Date(value);
    const days = Math.floor((Date.now() - then.getTime()) / 86400000);
    const relative = days === 0 ? 'today' : days === 1 ? 'yesterday' : `${days} days ago`;
    return `Last used ${relative} (${then.toLocaleDateString()})`;
  };

  return (
    <div className="settings-section">
      <h3>Connected Apps</h3>

      <p style={{ color: '#666', marginTop: 0 }}>
        AI assistants you have connected to SqueakBase. These connections are{' '}
        <strong>read-only</strong> — they can look at your colony but cannot add,
        change or delete anything.{' '}
        <a
          href="/guides/connect-claude/"
          target="_blank"
          rel="noopener noreferrer"
        >
          How to connect Claude
        </a>
      </p>

      {/* Shown whether or not anything is connected yet. This address is needed
          again every time another client is added — someone who has already
          connected claude.ai and now wants Claude Code needs it a second time —
          so hiding it once the list is non-empty strands exactly that user with
          nowhere in the product to look. Still conditional on the server having
          supplied it: printing an address that would not work is worse than
          printing none. */}
      {mcpUrl && (
        <div
          style={{
            background: '#f6f7f9',
            border: '1px solid #e4e4e8',
            borderRadius: '8px',
            padding: '12px 14px',
            margin: '0 0 16px',
            fontSize: '0.9em',
            color: '#555',
          }}
        >
          <div style={{ marginBottom: '4px' }}>
            Your connector address — paste this into Claude when adding a custom
            connector:
          </div>
          <code style={{ wordBreak: 'break-all', color: '#222' }}>{mcpUrl}</code>
        </div>
      )}

      {error && <div className="error">{error}</div>}

      {/* `error` is checked before the empty state on purpose. Both branches
          are reachable with connections === [], and rendering "No apps are
          connected" after a failed fetch asserts something we do not know —
          the dangerous direction to be wrong in, since it would reassure a
          user who came here specifically to check. */}
      {loading ? (
        <p>Loading…</p>
      ) : error ? null : connections.length === 0 ? (
        <div
          style={{
            border: '1px dashed #d0d0d5',
            borderRadius: '8px',
            padding: '20px',
            color: '#666',
          }}
        >
          <p style={{ margin: '0 0 8px' }}>No apps are connected.</p>
          {/* The address itself now lives above, where it stays reachable after
              the first connection. What is left here is the pointer to the
              walkthrough, which is what an empty state is actually for. */}
          <p style={{ margin: 0, fontSize: '0.9em' }}>
            To connect Claude, add SqueakBase as a custom connector in your
            Claude settings —{' '}
            <a href="/guides/connect-claude/" target="_blank" rel="noopener noreferrer">
              five-minute walkthrough
            </a>
            .
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {connections.map((connection) => (
            <div
              key={connection.client_id}
              style={{
                border: '1px solid #e4e4e8',
                borderRadius: '8px',
                padding: '14px 16px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '16px',
                flexWrap: 'wrap',
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600 }}>{connection.client_name}</div>
                <div style={{ fontSize: '0.88em', color: '#666', marginTop: '4px' }}>
                  {connection.view_mode === 'lab'
                    ? 'Can see: your lab’s shared data'
                    : 'Can see: your own data only'}
                </div>
                <div style={{ fontSize: '0.82em', color: '#888', marginTop: '2px' }}>
                  {formatUsed(connection.last_used_at)}
                  {connection.created_at &&
                    ` · Connected ${new Date(connection.created_at).toLocaleDateString()}`}
                </div>
              </div>

              <button
                className="btn-danger"
                onClick={() => setPendingDisconnect(connection)}
                disabled={disconnectingId === connection.client_id}
              >
                {disconnectingId === connection.client_id ? 'Disconnecting…' : 'Disconnect'}
              </button>
            </div>
          ))}
        </div>
      )}

      {pendingDisconnect && (
        <ConfirmDialog
          message={
            `Disconnect ${pendingDisconnect.client_name}? It will immediately lose ` +
            `access to your colony. You can reconnect it at any time by adding the ` +
            `connector again.`
          }
          confirmLabel="Disconnect"
          danger
          isProcessing={disconnectingId === pendingDisconnect.client_id}
          onConfirm={() => handleDisconnect(pendingDisconnect)}
          onCancel={() => setPendingDisconnect(null)}
        />
      )}
    </div>
  );
};

export default ConnectedApps;
